import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTrainingModules, updateTrainingProgress } from '../services/api';
import { TRAINING_MODULES as CONTENT_DATA } from '../data/trainingContent';
import { 
  ArrowLeft, BookOpen, Clock, CheckCircle, 
  Play, ChevronRight, ChevronLeft, Target, Award,
  List, Lightbulb, GraduationCap, BarChart3
} from 'lucide-react';

// Build a lookup map: skill_id -> content
const CONTENT_MAP = {};
CONTENT_DATA.forEach(m => { CONTENT_MAP[m.id] = m; });

function MarkdownContent({ text }) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    // Bold headers like **Title**
    if (/^\*\*\d+\.\s/.test(line.trim()) || /^\*\*[^*]+\*\*$/.test(line.trim())) {
      const headerText = line.replace(/\*\*/g, '').trim();
      elements.push(
        <h4 key={i} className="font-semibold text-slate-900 mt-5 mb-2 text-base">{headerText}</h4>
      );
      i++;
      continue;
    }

    // Bullet points
    if (/^[•\-\*]\s/.test(line.trim())) {
      const bullets = [];
      while (i < lines.length && /^[•\-\*]\s/.test(lines[i].trim())) {
        const bulletText = lines[i].trim().replace(/^[•\-\*]\s/, '');
        // Handle inline bold
        const parts = bulletText.split(/\*\*([^*]+)\*\*/);
        bullets.push(
          <li key={i} className="text-slate-600 leading-relaxed">
            {parts.map((part, idx) => idx % 2 === 1 ? <strong key={idx} className="text-slate-800">{part}</strong> : part)}
          </li>
        );
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="space-y-1 ml-4 list-disc marker:text-slate-400 mb-3">
          {bullets}
        </ul>
      );
      continue;
    }

    // Numbered lists
    if (/^\d+\.\s/.test(line.trim())) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^\d+\.\s/, '');
        const parts = itemText.split(/\*\*([^*]+)\*\*/);
        items.push(
          <li key={i} className="text-slate-600 leading-relaxed">
            {parts.map((part, idx) => idx % 2 === 1 ? <strong key={idx} className="text-slate-800">{part}</strong> : part)}
          </li>
        );
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="space-y-1 ml-4 list-decimal marker:text-slate-400 mb-3">
          {items}
        </ol>
      );
      continue;
    }

    // Italic hints like *Mitigation:*
    if (/^\*[^*]+\*/.test(line.trim())) {
      const italicText = line.trim().replace(/^\*|\*$/g, '');
      elements.push(
        <p key={i} className="text-sm italic text-teal-700 bg-teal-50 px-3 py-1.5 rounded mb-2">{italicText}</p>
      );
      i++;
      continue;
    }

    // Regular paragraph
    const parts = line.split(/\*\*([^*]+)\*\*/);
    elements.push(
      <p key={i} className="text-slate-600 leading-relaxed mb-3">
        {parts.map((part, idx) => idx % 2 === 1 ? <strong key={idx} className="text-slate-800">{part}</strong> : part)}
      </p>
    );
    i++;
  }

  return <div className="prose-content">{elements}</div>;
}

function ModuleOverview({ content, onStartLesson }) {
  return (
    <div className="space-y-6 animate-in">
      {/* Overview */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-600" />
          Overview
        </h3>
        <p className="text-slate-600 leading-relaxed">{content.overview}</p>
      </div>

      {/* Objectives */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Target className="w-5 h-5 text-emerald-600" />
          Learning Objectives
        </h3>
        <ul className="space-y-2">
          {content.objectives.map((obj, idx) => (
            <li key={idx} className="flex items-start gap-3 text-slate-600">
              <CheckCircle className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
              <span>{obj}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Lesson List */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <List className="w-5 h-5 text-violet-600" />
          Lessons ({content.lessons.length})
        </h3>
        <div className="space-y-2">
          {content.lessons.map((lesson, idx) => (
            <button
              key={lesson.id}
              onClick={() => onStartLesson(idx)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left group"
              data-testid={`lesson-item-${idx}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors">
                  {idx + 1}
                </span>
                <span className="text-slate-800 font-medium">{lesson.title}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Clock className="w-3.5 h-3.5" />
                <span>{lesson.duration} min</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={() => onStartLesson(0)}
        className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
        data-testid="begin-course-btn"
      >
        <Play className="w-5 h-5" />
        Begin Course
      </button>
    </div>
  );
}

function LessonView({ content, lessonIndex, onComplete, onBack, onNavigate, totalLessons, completedLessons, updating }) {
  const lesson = content.lessons[lessonIndex];
  const isLastLesson = lessonIndex === content.lessons.length - 1;
  const alreadyCompleted = lessonIndex < completedLessons;

  return (
    <div className="space-y-6 animate-in">
      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-sm text-slate-500 mb-2">
          <span>Lesson {lessonIndex + 1} of {totalLessons}</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {lesson.duration} min
          </span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${((lessonIndex + 1) / totalLessons) * 100}%` }}
          />
        </div>
      </div>

      {/* Lesson content */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="lesson-content">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
            <GraduationCap className="w-4 h-4" />
            Lesson {lessonIndex + 1}
          </div>
          <h2 className="font-outfit text-xl font-bold text-slate-900">{lesson.title}</h2>
        </div>

        <div className="px-6 py-6">
          <MarkdownContent text={lesson.content} />
        </div>

        {/* Key Takeaways */}
        {lesson.keyTakeaways && lesson.keyTakeaways.length > 0 && (
          <div className="mx-6 mb-6 p-5 bg-amber-50 border border-amber-200 rounded-xl">
            <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              Key Takeaways
            </h4>
            <ul className="space-y-2">
              {lesson.keyTakeaways.map((takeaway, idx) => (
                <li key={idx} className="flex items-start gap-2 text-amber-800 text-sm">
                  <span className="text-amber-500 mt-0.5 font-bold">{idx + 1}.</span>
                  <span>{takeaway}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-4">
        <button
          onClick={() => lessonIndex > 0 ? onNavigate(lessonIndex - 1) : onBack()}
          className="flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all font-medium"
          data-testid="prev-lesson-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          {lessonIndex > 0 ? 'Previous' : 'Overview'}
        </button>

        {alreadyCompleted && !isLastLesson ? (
          <button
            onClick={() => onNavigate(lessonIndex + 1)}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
            data-testid="next-lesson-btn"
          >
            Next Lesson <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={onComplete}
            disabled={updating}
            className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-all"
            data-testid="complete-lesson-btn"
          >
            {updating ? 'Saving...' : isLastLesson ? 'Complete Module' : 'Mark Complete & Continue'}
            <CheckCircle className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Lesson dots */}
      <div className="flex justify-center gap-2">
        {content.lessons.map((_, idx) => (
          <button
            key={idx}
            onClick={() => idx <= completedLessons ? onNavigate(idx) : null}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              idx === lessonIndex ? 'bg-blue-600 scale-125' : 
              idx < completedLessons ? 'bg-emerald-500 cursor-pointer hover:scale-110' : 
              'bg-slate-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function CompletionView({ content, onBack }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-10 text-center animate-in" data-testid="module-complete">
      <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-emerald-100 flex items-center justify-center">
        <Award className="w-10 h-10 text-emerald-600" />
      </div>
      <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-2">Module Complete!</h2>
      <p className="text-slate-500 mb-2">Congratulations on completing</p>
      <p className="text-lg font-semibold text-blue-700 mb-6">{content.title}</p>
      <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg text-sm font-medium mb-8">
        <CheckCircle className="w-4 h-4" />
        All {content.lessons.length} lessons completed
      </div>
      <div>
        <button
          onClick={onBack}
          className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
          data-testid="back-to-hub-btn"
        >
          Back to Training Hub
        </button>
      </div>
    </div>
  );
}

export default function TrainingPage() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // overview, lesson, complete
  const [currentLesson, setCurrentLesson] = useState(0);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await getTrainingModules();
        setModules(response.data.modules);
      } catch (error) {
        console.error('Failed to fetch modules:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchModules();
  }, []);

  const handleSelectModule = (module) => {
    setSelectedModule(module);
    setViewMode('overview');
    setCurrentLesson(0);
  };

  const handleStartLesson = (index) => {
    setCurrentLesson(index);
    setViewMode('lesson');
    window.scrollTo(0, 0);
  };

  const handleCompleteLesson = async () => {
    if (!selectedModule) return;
    setUpdating(true);
    try {
      const response = await updateTrainingProgress(selectedModule.id);
      const updatedProgress = response.data;

      setModules(prev => prev.map(m => 
        m.id === selectedModule.id 
          ? { ...m, completed_lessons: updatedProgress.completed_lessons, completed: updatedProgress.completed, progress_percent: updatedProgress.progress_percent }
          : m
      ));

      const content = CONTENT_MAP[selectedModule.id];
      if (currentLesson < content.lessons.length - 1) {
        setCurrentLesson(currentLesson + 1);
        window.scrollTo(0, 0);
      } else {
        setViewMode('complete');
      }
    } catch (error) {
      console.error('Failed to update progress:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleBackToHub = () => {
    setSelectedModule(null);
    setViewMode('overview');
  };

  const totalProgress = modules.length > 0 
    ? Math.round(modules.reduce((acc, m) => acc + m.progress_percent, 0) / modules.length)
    : 0;

  const completedModules = modules.filter(m => m.completed).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading training modules...</div>
      </div>
    );
  }

  // Module detail view
  if (selectedModule) {
    const content = CONTENT_MAP[selectedModule.id];
    if (!content) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <p className="text-slate-500">Module content not found.</p>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <button 
              onClick={() => viewMode === 'lesson' ? setViewMode('overview') : handleBackToHub()}
              className="p-2 hover:bg-slate-100 rounded-lg transition-all"
              data-testid="module-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </button>
            <div className="flex-1">
              <h1 className="font-outfit text-lg font-bold text-slate-900">{content.title}</h1>
              <p className="text-sm text-slate-500">{content.description}</p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <BarChart3 className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-700">{selectedModule.progress_percent}%</span>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8">
          {viewMode === 'overview' && (
            <ModuleOverview content={content} onStartLesson={handleStartLesson} />
          )}
          {viewMode === 'lesson' && (
            <LessonView
              content={content}
              lessonIndex={currentLesson}
              onComplete={handleCompleteLesson}
              onBack={() => setViewMode('overview')}
              onNavigate={(idx) => { setCurrentLesson(idx); window.scrollTo(0, 0); }}
              totalLessons={content.lessons.length}
              completedLessons={selectedModule.completed_lessons}
              updating={updating}
            />
          )}
          {viewMode === 'complete' && (
            <CompletionView content={content} onBack={handleBackToHub} />
          )}
        </main>
      </div>
    );
  }

  // Training Hub (grid of modules)
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div>
              <h1 className="font-outfit text-xl font-bold text-slate-900">Training Hub</h1>
              <p className="text-sm text-slate-500">10 Skills of School Risk Intelligence</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Overview Stats */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-8" data-testid="training-overview">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="font-outfit text-2xl font-semibold text-slate-900 mb-1">Your Progress</h2>
              <p className="text-slate-500">Complete all 10 modules to master risk intelligence</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-outfit font-bold text-emerald-600">{completedModules}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-outfit font-bold text-blue-600">{totalProgress}%</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Overall</p>
              </div>
            </div>
          </div>
          <div className="mt-4 h-3 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        {/* Module Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {modules.map((module, index) => {
            const content = CONTENT_MAP[module.id];
            return (
              <div 
                key={module.id}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md transition-all animate-in cursor-pointer group"
                style={{ animationDelay: `${index * 40}ms` }}
                onClick={() => handleSelectModule(module)}
                data-testid={`module-card-${module.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: content?.color || '#2563EB' }}
                  >
                    {index + 1}
                  </div>
                  {module.completed && (
                    <div className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span className="text-xs font-semibold">Complete</span>
                    </div>
                  )}
                </div>

                <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-1 group-hover:text-blue-700 transition-colors">{module.title}</h3>
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">{module.description}</p>

                <div className="flex items-center gap-4 text-sm text-slate-400 mb-4">
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-3.5 h-3.5" /> {content?.lessons.length || module.lessons} lessons
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {content?.duration || module.duration} min
                  </span>
                </div>

                <div className="mb-3">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>{module.completed_lessons}/{module.lessons} lessons</span>
                    <span>{module.progress_percent}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 transition-all duration-300"
                      style={{ width: `${module.progress_percent}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-all border border-transparent group-hover:border-blue-200 group-hover:bg-blue-50 text-blue-600">
                  {module.completed ? (
                    <>Review Module</>
                  ) : module.completed_lessons > 0 ? (
                    <>Continue <ChevronRight className="w-4 h-4" /></>
                  ) : (
                    <>Start Course <Play className="w-4 h-4" /></>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
